import { Link } from 'react-router-dom'
import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'

import {
	formatDateTime,
	getFirstRelationValue,
	getPhoneNumber,
	getRelationId,
	getRelationValue,
} from './rozmowaDetailsHelpers'

export default function RozmowaDetailsCard({ rozmowa, onBack, onEdit, onDelete, deleting }) {
	const { t } = useTranslation()
	const placowka = rozmowa?.id_placowka || rozmowa?.placowka
	const osobaId = getRelationId(rozmowa?.id_osoba || rozmowa?.osoba)
	const placowkaId = getRelationId(placowka)
	const osobaName = rozmowa?.imie_i_nazwisko || getRelationValue(rozmowa?.id_osoba || rozmowa?.osoba)
	const placowkaName = rozmowa?.placowkaName || rozmowa?.placowka_nazwa || getRelationValue(placowka)
	const userName = getRelationValue(rozmowa?.id_user)
	const miastoName = getFirstRelationValue(rozmowa?.id_miasto, rozmowa?.miasto, placowka?.id_miasto, placowka?.miasto)
	const gminaName = getFirstRelationValue(rozmowa?.id_gmina, rozmowa?.gmina, placowka?.id_gmina, placowka?.gmina)
	const powiatName = getFirstRelationValue(rozmowa?.id_powiat, rozmowa?.powiat, placowka?.id_powiat, placowka?.powiat)
	const wojewodztwoName = getFirstRelationValue(
		rozmowa?.id_wojewodztwo,
		rozmowa?.wojewodztwo,
		placowka?.id_wojewodztwo,
		placowka?.wojewodztwo
	)
	const phoneNumber = getPhoneNumber(rozmowa)

	return (
		<div className="mb-5 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="text-lg font-semibold text-gray-900">{t('conversations.details.title')}</h1>
					<p className="mt-1 text-sm text-gray-600">{formatDateTime(rozmowa?.dig_rozmowy)}</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{onEdit && (
						<button
							type="button"
							onClick={onEdit}
							className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100">
							<PencilIcon className="h-4 w-4" />
							<span>{t('conversations.details.actions.editConversation')}</span>
						</button>
					)}
					{onDelete && (
						<button
							type="button"
							onClick={onDelete}
							disabled={deleting}
							className="inline-flex items-center gap-1 rounded-md border border-red-100 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60">
							<TrashIcon className="h-4 w-4" />
							<span>
								{deleting ? t('conversations.details.actions.deleting') : t('conversations.details.actions.delete')}
							</span>
						</button>
					)}
					<button
						type="button"
						onClick={onBack}
						className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
						{t('conversations.details.actions.back')}
					</button>
				</div>
			</div>
			<dl className="mt-4 grid grid-cols-1 gap-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.conversationDate')}</dt>
					<dd className="mt-0.5 text-gray-900">{formatDateTime(rozmowa?.dig_rozmowy)}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.person')}</dt>
					<dd className="mt-0.5 text-gray-900">
						{osobaId ? (
							<Link to={`/klienci/osoba/details/${osobaId}`} className="text-blue-600 hover:text-blue-800 hover:underline">
								{osobaName}
							</Link>
						) : (
							osobaName
						)}
					</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.facility')}</dt>
					<dd className="mt-0.5 text-gray-900">
						{placowkaId ? (
							<Link to={`/klienci/placowki/details/${placowkaId}`} className="text-blue-600 hover:text-blue-800 hover:underline">
								{placowkaName}
							</Link>
						) : (
							placowkaName
						)}
					</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.user')}</dt>
					<dd className="mt-0.5 text-gray-900">{userName}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.phone')}</dt>
					<dd className="mt-0.5 text-gray-900">{phoneNumber || '-'}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.city')}</dt>
					<dd className="mt-0.5 text-gray-900">{miastoName}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.commune')}</dt>
					<dd className="mt-0.5 text-gray-900">{gminaName}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.district')}</dt>
					<dd className="mt-0.5 text-gray-900">{powiatName}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.voivodeship')}</dt>
					<dd className="mt-0.5 text-gray-900">{wojewodztwoName}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('conversations.details.fields.created')}</dt>
					<dd className="mt-0.5 text-gray-900">{formatDateTime(rozmowa?.created)}</dd>
				</div>
			</dl>
		</div>
	)
}
